<template>
    <admin-layout 
    title="View Package"
    show-back
    backUrl="/contents/packages">
        <div>
            <TabComponent
                :tabs="tabs"
            >
            </TabComponent>
        </div>
        <div class="p-7">
            <FormSection>
                <template #title>
                    Package Information
                </template>

                <template #description>
                    Relevant data and other details

                </template>

                <template #form>
                    <div class="col-span-12">
                        <TextInput 
                            id="package_name"
                            name="package_name"
                            disabled
                            label="Package Name"
                            placeholder="Package Name"
                            v-model="form.package_name"
                        />
                    </div>
                    <div class="col-span-12">
                        <TextInput 
                            id="package_type"
                            name="package_type"
                            disabled
                            label="Package Type"
                            placeholder="Package Type"
                            v-model="form.package_type"
                        />
                    </div>

                    <template v-for="package_line in form.package_line" :key="package_line">
                        <div class="col-span-12">
                            <TextInput 
                                :modelValue="package_line.items.item_cat.item_category"
                                :id="package_line.item_id + 'item_category'"
                                :name="package_line.item_id + 'item_category'"
                                disabled
                                label="Item Category"
                                placeholder="Item Category"
                            />
                        </div>
                        <div class="col-span-12">
                            <TextInput 
                                :modelValue="package_line.items.item_name"
                                :id="package_line.item_id + 'item_name'"
                                :name="package_line.item_id + 'item_name'"
                                disabled
                                label="Item"
                                placeholder="Item"
                            />
                        </div> 
                    </template>


                    <div class="col-span-12">
                        <TextInput 
                            id="rider_name"
                            name="rider_name"
                            disabled
                            label="Rider Name"
                            placeholder="Rider Name"
                            v-model="form.rider_name"
                        />
                    </div>
                    
                    <div class="col-span-6">
                        <TextInput
                            id="package_fee"
                            name="package_fee"
                            v-model="form.package_fee"
                            disabled
                            add-on-left
                            add-on-text="₱" 
                            label="Package Fee"
                            placeholder="Package Fee"
                        />
                    </div>

                    <div class="col-span-12">

                        <MultiselectInput
                            v-model="form.associated_company"
                            id="associated_company"
                            name="associated_company"
                            label="Participating Companies"
                            placeholder="Participating Companies"
                            searchable
                            mode="tags"
                            :options="companies"
                        />  
                    </div>


                    <div class="col-span-12">
                        <!-- :disabled="$hasPermission('upload-pdf-file')" -->
                        <DropzoneInput 
                            label="Attachments"
                            v-model:path="form.file_attachment"
                            v-model:file="form.file_attachment"
                            accept="application/pdf"
                            description="Max File Size: 2MB · File Type: PDF"
                            :ext-source="extBase64Data"
                            :ext-file-type="extFileType"
                            :ext-file-name="extFileName"
                            :ext-file-size="extFileSize"
                            @delete:file="removeAttachment()"
                        />

                    </div>
                </template>

                <template #actions>
                    <ButtonComponent @click="submitPackages">
                        Save Changes
                    </ButtonComponent>
                </template>
            </FormSection>
        </div>

        <SuccessModal
            :show="showSuccess"
            @cancel="closeSuccessModal()"
            @confirm="closeSuccessModal()"
            action-text="Close"
            title="Package Updated"
            content="Package updated Successfully!"
        />

        <ErrorModal
            width="w-[548px]"
            title="Warning"
            action-text="Close"
            cancel-text="Go back"
            :show="showError"
            @close="showError = false"
            @confirm="showError = false"
        >
            <template #content>
                {{ errorMsg }}
            </template>
        </ErrorModal>

        <PageLoader v-if="loader" />

    </admin-layout>
</template>

<script setup lang="ts">
import AdminLayout from '@/layouts/AdminLayout.vue';
import TabComponent from '@/components/TabComponent.vue';
import FormSection from '@/components/FormSection.vue';
import TextInput from '@/components/inputs/TextInput.vue';
import SuccessModal from '@/components/modal/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import PageLoader from '@/components/PageLoader.vue';
import DropzoneInput from '@/components/inputs/DropzoneInput.vue';
import ButtonComponent from '@/components/ButtonComponent.vue';
import MultiselectInput from '@/components/inputs/MultiselectInput.vue';

import Packages from '@/classes/Packages';
import DropdownData from '@/classes/DropdownData';
import { useRoute } from 'vue-router';
import { ref, onMounted, watch } from 'vue';
import { checkForInvalidResponse } from '@/helpers/PageHelper';


/**
 * ********************************
 * Data 
 * ********************************
 */
const route       = useRoute();
const form        = ref({})
const loader      = ref(false)
const showError   = ref(false);
const packageName = ref(route.params.name);
const companies     = ref([]);
const attachedFile = ref();
const errorMsg         = ref()
const errors      = ref([])
const showSuccess      = ref(false)
const showConfirmation = ref(false)

const extBase64Data = ref<any>(null);
const extFileType = ref<any>(null);
const extFileName = ref<any>(null);
const extFileSize = ref<any>(null);

const tabs: { name: string, href: string, allowed: boolean }[] = [
    {
        name: 'Information',
        href: `/contents/packages/show/${packageName.value}`,
        allowed: true
    },
    {
        name: 'Schedule',
        href: `/contents/packages/schedule/${packageName.value}`,
        allowed:
            window.$hasPermission('update-package-and-rider-schedule')
    },
];

/**
 * ********************************
 * Fetch Data 
 * ********************************
 */

const fetchPackage = async () => {
    try {
        loader.value = true;
        const response   = await Packages.showPackage(packageName.value);
        const packageFee = response.data[0].package_line.reduce((a,r) => a + parseFloat(r.item_fee), 0);
        const associated_company = response.data[0].associated_company.map((item:any) => parseInt(item.company_id));

        attachedFile.value = response.data[0].file_attachment;
        let fileAttachment = null;
        
        if (response.data[0].file_attachment) {
            const base64Data =  response.data[0].file_attachment;

            extBase64Data.value = base64Data;
            extFileType.value = 'application/pdf';
            extFileName.value = packageName.value;
            const paddedLength = base64Data.length - (base64Data.endsWith('==') ? 2 : base64Data.endsWith('=') ? 1 : 0);
            extFileSize.value = Math.floor((paddedLength * 3) / 4);

            // Check if it's a base64 string
            if (typeof base64Data === 'string' && base64Data.startsWith('data:')) {
                // Get the MIME type from the base64 string
                const mimeType = base64Data.split(';')[0].split(':')[1];
                // Convert base64 to blob
                const byteCharacters = atob(base64Data.split(',')[1]);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: mimeType });
                
                // Create File object from Blob
                fileAttachment = new File([blob], packageName.value, { type: mimeType });
            }
        }

        // let fileAttachment = null;
        // if (response.data[0].file_attachment) {
        //     const base64Data = response.data[0].file_attachment;

        //     extBase64Data.value = base64Data;
        //     extFileType.value = 'application/pdf';
        //     extFileName.value = packageName.value;
        //     const paddedLength = base64Data.length - (base64Data.endsWith('==') ? 2 : base64Data.endsWith('=') ? 1 : 0);
        //     extFileSize.value = Math.floor((paddedLength * 3) / 4);

        //     // Check if it's a base64 string
        //     if (typeof base64Data === 'string' && base64Data.startsWith('data:')) {
        //         // Get the MIME type from the base64 string
        //         const mimeType = base64Data.split(';')[0].split(':')[1];
        //         // Convert base64 to blob
        //         const byteCharacters = atob(base64Data.split(',')[1]);
        //         const byteNumbers = new Array(byteCharacters.length);
        //         for (let i = 0; i < byteCharacters.length; i++) {
        //             byteNumbers[i] = byteCharacters.charCodeAt(i);
        //         }
        //         const byteArray = new Uint8Array(byteNumbers);
        //         const blob = new Blob([byteArray], { type: mimeType });
                
        //         // Create File object from Blob
        //         fileAttachment = new File([blob], packageName.value, { type: mimeType });
        //     }
        // }

        const data = {
            package_name: response.data[0].package_name,
            package_id  : response.data[0].package_id,
            package_code: response.data[0].package_code,
            package_line: response.data[0].package_line,
            package_type: response.data[0].package_type,
            package_fee : packageFee,
            associated_company : associated_company ?? [],
            file_attachment: fileAttachment,
        };

        Object.assign(form.value, data);

    } catch (error) {
        loader.value    = false;
        showError.value = true;
        errorMsg.value  = error?.response?.data?.message || 'Error occurred, please try again';

        checkForInvalidResponse(error, '/contents/packages')
    }
    finally{
        loader.value = false
    }
}

/**
 * Fetch companies
 */
const fetchCompanies = async () => {
    const response = await DropdownData.getCompanies();
    companies.value = response.data.response.map((company: any) => ({
        id: company.id,
        label: company.name,
        value: company.id
    }));
}

const submitPackages = async () => {
    loader.value = true;

    const packageId = form.value.package_id;

    if (!form.value.file_attachment) {
        showError.value = true;
        errorMsg.value = "Please attach a file before submitting.";
        loader.value = false;
        return;
    }

    if (!form.value.associated_company || form.value.associated_company.length === 0) {
        showError.value = true;
        errorMsg.value = "Please select at least one participating company.";
        loader.value = false;
        return;
    }

    try {
        const payload = {
            package_id: packageId,
            file_attachment: null,
            associated_company: [],
        };
        
        // Append the file
        if (form.value.file_attachment instanceof File) {
            const base64File = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                    resolve(reader.result);
                };
                reader.onerror = reject;
                reader.readAsDataURL(form.value.file_attachment);
            });

            payload.file_attachment = base64File;
        }
        
        // Append participating companies
        if (form.value.associated_company || form.value.associated_company.length > 0) {
            const formattedCompanies = form.value.associated_company.map(selectedId => {
            const companyDetails = companies.value.find(company => company.id === parseInt(selectedId));
                return {
                    company_id: parseInt(selectedId),
                    company_name: companyDetails?.label ?? 'Company Name'
                };
            });

            payload.associated_company = formattedCompanies;
        }

        const response = await Packages.updatePackage(payload);

        if (response.data.response && response.data.result_status === 200 && response.data.message === 'Success') {
            console.log('Package updated successfully');
            showSuccess.value = true;
        } else {
            showError.value = true;
            errorMsg.value = "Failed to update the package. Please try again.";
        }
    } catch (error) {
        console.log(error);
        showError.value = true;
        errorMsg.value = error.response?.data?.message || "An error occurred. Please try again later.";
        if (error.response?.status === 422) {
            errors.value = error.response.data.errors;
        }
        if (error.response?.status === 401) {
            window.location.replace('/login');
        }
    } finally {
        loader.value = false;
    }
}

const stringToBlob = (content: string, mimeType: string): Blob => {
    // Convert the string to a Blob
    const blob = new Blob([content], { type: mimeType });
    return blob;
}

const getFileTypeFromString = (content: string): string => {
    // This is a simple approach to guess the file type based on the content
    // You might want to expand this for more accurate detection
    if (content.startsWith('<?xml')) {
        return 'application/xml';
    } else if (content.startsWith('{') || content.startsWith('[')) {
        return 'application/json';
    } else if (content.startsWith('<!DOCTYPE html>') || content.includes('<html')) {
        return 'text/html';
    } else {
        return 'text/plain'; // Default to plain text if unable to determine
    }
}

const closeSuccessModal = ():void => {
    location.reload();
}

const removeAttachment = () => {
    form.value.file_attachment = null;
    extBase64Data.value = null;
    extFileType.value = null;
    extFileName.value = null;
    extFileSize.value = null;
    console.log('Attachment removed');
}

/**
 * ********************************
 * Watchers
 * ********************************
 */

// Watch for changes to the attachedFile
watch(() => form.value.file_attachment, (newFile) => {
  if (newFile) {
    console.log('New file attached (from watch):', newFile);
  } else {
    console.log('No file attached (from watch)');
  }
}, { deep: true });

onMounted(() => {
    fetchPackage();
    fetchCompanies();
})

</script>